// hooks/useMeasurements.js - Enhanced with individual sensor measurements
import { useCallback } from 'react';
import { useApi } from './useApi';
import { measurementsService } from '../services/measurementsService';

// Hook for getting latest measurements
export function useLatestMeasurements(boxId, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId) return Promise.reject(new Error('Box ID is required'));
    return measurementsService.getLatestMeasurements(boxId);
  }, [boxId]);

  return useApi(apiFunction, [boxId], {
    immediate: !!boxId,
    ...options
  });
}

// Hook for getting box measurements with parameters
export function useBoxMeasurements(boxId, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId) return Promise.reject(new Error('Box ID is required'));
    return measurementsService.getBoxMeasurements(boxId, params);
  }, [boxId, params]);

  return useApi(apiFunction, [boxId, params], {
    immediate: !!boxId,
    ...options
  });
}

// Hook for measurements with date range
export function useMeasurementsInRange(boxId, startDate, endDate, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId || !startDate || !endDate) {
      return Promise.reject(new Error('Box ID, start date, and end date are required'));
    }
    return measurementsService.getMeasurementsInRange(boxId, startDate, endDate, params);
  }, [boxId, startDate, endDate, params]);

  return useApi(apiFunction, [boxId, startDate, endDate, params], {
    immediate: !!(boxId && startDate && endDate),
    ...options
  });
}

// Hook for paginated measurements
export function useMeasurementsPaginated(boxId, page = 1, limit = 100, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId) return Promise.reject(new Error('Box ID is required'));
    return measurementsService.getMeasurementsPaginated(boxId, page, limit, params);
  }, [boxId, page, limit, params]);

  return useApi(apiFunction, [boxId, page, limit, params], {
    immediate: !!boxId,
    ...options
  });
}

// NEW: Hook for individual sensor measurements with time range
export function useSensorMeasurementsWithRange(boxId, sensorId, startDate, endDate, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId || !sensorId) {
      return Promise.reject(new Error('Box ID and sensor ID are required'));
    }
    return measurementsService.getSensorMeasurementsWithRange(boxId, sensorId, startDate, endDate, params);
  }, [boxId, sensorId, startDate, endDate, params]);

  return useApi(apiFunction, [boxId, sensorId, startDate, endDate, params], {
    immediate: !!(boxId && sensorId),
    ...options
  });
}

// NEW: Hook for individual sensor measurements with automatic time range (last 24 hours)
export function useSensorMeasurements24h(boxId, sensorId, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId || !sensorId) {
      return Promise.reject(new Error('Box ID and sensor ID are required'));
    }

    // Calculate 24 hours ago
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);

    return measurementsService.getSensorMeasurementsWithRange(boxId, sensorId, startDate, endDate, params);
  }, [boxId, sensorId, params]);

  return useApi(apiFunction, [boxId, sensorId, params], {
    immediate: !!(boxId && sensorId),
    ...options
  });
}

// NEW: Hook for individual sensor measurements with configurable time range
export function useSensorMeasurementsWithDuration(boxId, sensorId, durationHours = 24, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId || !sensorId) {
      return Promise.reject(new Error('Box ID and sensor ID are required'));
    }

    // Calculate time range based on duration
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - durationHours * 60 * 60 * 1000);

    return measurementsService.getSensorMeasurementsWithRange(boxId, sensorId, startDate, endDate, params);
  }, [boxId, sensorId, durationHours, params]);

  return useApi(apiFunction, [boxId, sensorId, durationHours, params], {
    immediate: !!(boxId && sensorId),
    ...options
  });
}

// Hook for sensor-specific measurements (existing - for compatibility)
export function useSensorMeasurements(boxId, sensorName, params = {}, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId || !sensorName) {
      return Promise.reject(new Error('Box ID and sensor name are required'));
    }
    return measurementsService.getSensorMeasurements(boxId, sensorName, params);
  }, [boxId, sensorName, params]);

  return useApi(apiFunction, [boxId, sensorName, params], {
    immediate: !!(boxId && sensorName),
    ...options
  });
}

// Hook for checking measurement availability
export function useMeasurementAvailability(boxId, options = {}) {
  const apiFunction = useCallback(() => {
    if (!boxId) return Promise.reject(new Error('Box ID is required'));
    return measurementsService.checkMeasurementAvailability(boxId);
  }, [boxId]);

  return useApi(apiFunction, [boxId], {
    immediate: !!boxId,
    ...options
  });
}